import { useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2, AlertTriangle, ShieldAlert, Sparkles, Building2, User, Wrench, TrendingUp, DollarSign, Calendar, ScanLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_ORDER = [
  { key: "who_is_affected", label: "Who is affected", icon: User },
  { key: "what_happened", label: "What happened", icon: AlertTriangle },
  { key: "how_attackers_operate", label: "How attackers operate", icon: ScanLine },
  { key: "business_impact", label: "Business impact", icon: Building2 },
  { key: "consumer_impact", label: "Consumer impact", icon: User },
  { key: "expert_analysis", label: "Expert analysis", icon: Sparkles },
  { key: "estimated_financial_damage", label: "Estimated financial damage", icon: DollarSign },
  { key: "risk_forecast_30d", label: "Risk forecast — next 30 days", icon: Calendar },
];

export default function CyberIntelModal({ articleId, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setErr("");
    axios.get(`${API}/cyber/article/${articleId}/intel`, { timeout: 60000 })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Could not load intel briefing."); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [articleId]);

  const intel = data?.intel;
  const art = data?.article;
  const band = intel?.severity_band;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60" onClick={onClose} data-testid="cyber-intel-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-3xl rounded-t-2xl sm:rounded-2xl overflow-hidden max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start gap-3 p-5 border-b border-[var(--line)] bg-gradient-to-br from-[#0a1f3d] to-[#0f2a5f] text-white">
          <ShieldAlert size={20} className="mt-1 text-[var(--gold)] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--gold)] mb-1">
              Newseze Cyber Intelligence Center
            </div>
            <h2 className="text-[17px] font-black leading-tight" data-testid="cyber-intel-title">
              {art?.title || "Loading…"}
            </h2>
            {art?.source && <div className="text-[11px] opacity-80 mt-1">{art.source}</div>}
          </div>
          <button onClick={onClose} aria-label="Close" className="text-white hover:opacity-80" data-testid="cyber-intel-close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-5 space-y-4">
          {busy && (
            <div className="flex items-center gap-2 text-[var(--muted)] text-[13px]" data-testid="cyber-intel-loading">
              <Loader2 size={14} className="animate-spin" /> Newseze's algorithm is reading the source and building the intel briefing…
            </div>
          )}
          {err && (
            <div className="text-rose-700 text-[13px] flex items-center gap-2" data-testid="cyber-intel-error">
              <AlertTriangle size={14} /> {err}
            </div>
          )}
          {data && !intel && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-900 p-3 text-[13px]" data-testid="cyber-intel-unavailable">
              {data.message || "Intel briefing temporarily unavailable."}
            </div>
          )}

          {intel && (
            <>
              {/* Severity hero */}
              <div className="rounded-xl border-2 p-4 flex items-center gap-4" style={{ borderColor: band?.color }} data-testid="cyber-severity-hero">
                <div className="w-20 h-20 rounded-xl flex items-center justify-center text-white text-[28px] font-black shrink-0" style={{ backgroundColor: band?.color }}>
                  {intel.severity_score}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--muted)]">Cyber severity score · 1–100</div>
                  <div className="text-[20px] font-black tracking-tight" style={{ color: band?.color }} data-testid="cyber-severity-label">
                    {band?.icon} {band?.label}
                  </div>
                  <div className="text-[12.5px] text-[var(--ink)] mt-0.5"><strong>Reader action:</strong> {band?.action}</div>
                </div>
                <div className="text-[11px] uppercase tracking-[0.14em] font-black bg-[var(--ink)] text-white px-2 py-1 rounded shrink-0" data-testid="cyber-threat-type">
                  {intel.threat_type?.replace(/_/g, " ")}
                </div>
              </div>

              {/* What to do now */}
              {Array.isArray(intel.what_to_do_now) && intel.what_to_do_now.length > 0 && (
                <div className="rounded-xl bg-[#fff7d6] border-2 border-[var(--gold)] p-4" data-testid="cyber-actions">
                  <div className="flex items-center gap-1.5 mb-2 text-[11px] uppercase tracking-[0.16em] font-black text-[#1d1303]">
                    <Wrench size={12} /> What to do now
                  </div>
                  <ol className="space-y-1.5 list-decimal pl-5">
                    {intel.what_to_do_now.map((a, i) => (
                      <li key={i} className="text-[13.5px] font-bold text-[#1d1303]" data-testid={`cyber-action-${i}`}>{a}</li>
                    ))}
                  </ol>
                </div>
              )}

              {/* All 8 narrative fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {FIELD_ORDER.filter((f) => intel[f.key]).map((f) => {
                  const IconCls = f.icon;
                  return (
                    <div key={f.key} className="rounded-lg border border-[var(--line)] p-3" data-testid={`cyber-field-${f.key}`}>
                      <div className="flex items-center gap-1.5 mb-1.5 text-[10px] uppercase tracking-[0.14em] font-black text-[var(--blue)]">
                        <IconCls size={11} /> {f.label}
                      </div>
                      <p className="text-[13px] text-[var(--ink)] leading-snug whitespace-pre-wrap">{intel[f.key]}</p>
                    </div>
                  );
                })}
              </div>

              <p className="text-[10.5px] text-[var(--muted)] italic border-t border-[var(--line)] pt-3">
                Intel generated by Newseze's algorithm under fair use (17 U.S.C. § 107). Not security, legal, or financial advice — verify with the original source and your IT/security team before acting.
                {data?.cached === false && intel?.generated_at && ` · Generated ${new Date(intel.generated_at).toLocaleString()}`}
                {data?.cached === true && ` · Cached briefing`}
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[var(--line)] flex items-center justify-between gap-2">
          {art?.external_link && (
            <a href={art.external_link} target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer"
               className="text-[12px] text-[var(--blue)] underline font-bold" data-testid="cyber-intel-source-link">
              Read original at {art.source}
            </a>
          )}
          <button onClick={onClose} className="btn ml-auto" data-testid="cyber-intel-done">
            <TrendingUp size={13} /> Done
          </button>
        </div>
      </div>
    </div>
  );
}
